import React from 'react'
import { Link, Heading, Accordion, AccordionTab, BodyText } from '@lmig/lmds-react'
import './FAQ.css'

function FAQ() {
  const faqList = [
    {
      id: 0,
      title: 'Getting Your 1099',
      desc: (
        <BodyText type='default'>
          <span>
            <br />
            <h3>When and where will my 1099 be available?</h3> <br />
            <ul>
              <li>
                When a 1099 form is required, it will be mailed no later than the IRS deadline. If the total amount you
                received is below the $2,000 reporting threshold — excluding certain 1099-MISC categories such as Box 6
                and Box 10 — a 1099 will not be issued. (Note: this threshold increased from $600 to $2,000 beginning
                with tax year 2026 — if you received a 1099 last year but not this year).
              </li>
              <br />
              <li>
                If you recently moved, your 1099 may have been mailed to your previous address. To update your address
                or request a reprint, please submit a “
                <Link href={'https://1099portal.libertymutual.com/form/3'} target='_blank' >
                  Request an Address Change or 1099 Reprint
                </Link>
                ” request at the top of this page.
              </li>
              <br />
              <li>
                If you believe you should have received a 1099 but have not, please submit a “
                <Link href={'https://1099portal.libertymutual.com/form/4'} target='_blank' >
                  Request a 1099
                </Link>
                ” ticket.
              </li>
              <br />
              <li>
                <b>Note:</b> If you are a Safeco agent or general agent, we are not required to issue a 1099 form.
                Please refer to your year-end commission statement for your records. Safeco agents can access their
                statements here:{' '}
                <Link
                  href={'https://safesite.safeco.com/safeconow/sessionexpiration/PINGLockUserErrorTemplate.html#.'}
                  target='_blank'

                >
                  https://agent.safeco.com/salescompreporting/pages/start.aspx
                </Link>
              </li>
            </ul>
          </span>
          <span>
            <br />
            <h3>How will I receive my 1099?</h3> <br />
            <ul>
              <li>
                Your 1099 is mailed to the address we have on file for you by the IRS deadline. Electronic delivery
                isn’t currently available — mail is the only delivery method. If you need to update your mailing
                address, please submit a “
                <Link href={'https://1099portal.libertymutual.com/form/3'} target='_blank' >
                  Request an Address Change or 1099 Reprint
                </Link>
                ” request at the top of this page.
              </li>
            </ul>
          </span>
          <span>
            <br />
            <h3>Can I get a duplicate copy of my 1099?</h3> <br />
            <ul>
              <li>
                Yes — please submit a “
                <Link href={'https://1099portal.libertymutual.com/form/4'} target='_blank' >
                  Request a 1099
                </Link>
                ” ticket. Please make sure the information you provide is Updated/Recent/Latest.
              </li>
            </ul>
          </span>
          <span>
            <br />
            <h3>What should I do if I did not receive a 1099?</h3> <br />
            <ul>
              <li>
                If you believe you should have received a 1099 but have not, please submit a “
                <Link href={'https://1099portal.libertymutual.com/form/4'} target='_blank' >
                  Request a 1099
                </Link>
                ” ticket.
              </li>
            </ul>
          </span>
          <span>
            <br />
            <h3>Where can I find my 1099 details?</h3> <br />
            <ul>
              <li>
                The back of your 1099 shows a breakdown of the payments included in your reported total. Because space
                is limited, only one page of payment detail can be shown — if your transactions don’t all fit, a note
                will appear at the end of the section letting you know some are not listed.
              </li>
              <br />
              <li>
                If you need a full transaction report, please submit a “{' '}
                <Link href={'https://1099portal.libertymutual.com/form/2'} target='_blank' >
                  Request Additional Information
                </Link>
                ” request at the top of this page.
              </li>
            </ul>
          </span>
        </BodyText>
      ),
    },
    {
      id: 1,
      title: 'Understanding Your 1099',
      desc: (
        <BodyText type='default'>
          <span>
            <br />
            <h3>Why did I receive a 1099 from Liberty Mutual?</h3>
            <br />
            <ul>
              <li>
                You received a 1099 because Liberty Mutual (or one of its subsidiaries) paid you during the year that
                the IRS requires us to report — for example, commissions, settlement payments, or interest. Which form
                you received (1099-NEC, 1099-MISC, or 1099-INT) depends on the type of payment.
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>What is a Form 1099-NEC and why did I receive one?</h3>
            <br />
            <ul>
              <li>
                A 1099-NEC (Non-employee Compensation) reports payments of $2,000 or more made to people who are not
                employees — such as independent contractors, freelancers, and other self-employed individuals — for
                services performed during the year. (Note: the reporting threshold increased from $600 to $2,000
                beginning with tax year 2026.)
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>What is a Form 1099-MISC and why did I receive one?</h3>
            <br />
            <ul>
              <li>
                A 1099-MISC reports miscellaneous income paid to non-employees that isn’t covered by a more specific
                form (such as 1099-NEC for non-employee compensation).
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>Why does the amount on my 1099 differ from my records?</h3>
            <br />
            <ul>
              <li>
                Your 1099 amount can differ from your own records because we report payments based on the date they were
                issued, not the date they were received — this is most noticeable for payments issued near year-end.
              </li>
              <br />
              <li>
                If you are a broker, differences can also happen because of prior year policy cancellation adjustments.
              </li>
              <br />
              <li>
                If you need further research or clarification, please submit a “
                <Link href='https://1099portal.libertymutual.com/form/2' target='_blank' >
                  Request Additional Information
                </Link>
                ” request at the top of this page.
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>What payments are included on my 1099?</h3>
            <br />
            <ul>
              <li>
                Your 1099 includes reportable payments made to you during the calendar year — for example, commissions,
                settlements, attorney fees, or interest. It does not include payments below the IRS reporting threshold
                or payments that aren’t classified as reportable income. For a breakdown of your specific payments, see
                the detail section on the back of your 1099.
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>Are taxes withheld from the payments reported on my 1099?</h3>
            <br />
            <ul>
              <li>
                Generally, no federal or state income tax is withheld from 1099 payments. The exception is backup
                withholding, which applies if we don’t have a valid W-9 on file for you — regardless of the payment
                amount. If backup withholding is applied to your payments, the amount withheld will be shown in Box 4 of
                your 1099.
              </li>
            </ul>
          </span>
        </BodyText>
      ),
    },
    {
      id: 2,
      title: 'Corrections and Updates',
      desc: (
        <BodyText type='default'>
          <span>
            <br />
            <h3>What do I do if my recipient name or TIN is incorrect?</h3>
            <br />
            <ul>
              <li>
                Please submit a signed W-9 form showing your correct information (exempt recipients should ensure that
                Box 4, the exemption box, is fully completed), along with a copy of the incorrect 1099. You can submit
                these documents at the top of this page under “
                <Link href={'https://1099portal.libertymutual.com/form/1'} target='_blank' >
                  Request a Name or Tax ID Change
                </Link>
                .” For a blank W-9, please visit the <Link href={'https://www.irs.gov/pub/irs-pdf/fw9.pdf'} target='_blank' >IRS website</Link>.
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>What do I do if my address is incorrect?</h3>
            <br />
            <ul>
              <li>
                Please submit a “
                <Link href={'https://1099portal.libertymutual.com/form/3'} target='_blank' >
                  Request an Address Change or 1099 Reprint
                </Link>
                ” request at the top of this page. Once your address is updated, we can reissue your 1099 to the correct
                address.
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>How do I request a corrected 1099?</h3>
            <br />
            <ul>
              <li>
                If your 1099 has an error (for example, an incorrect amount, name, or TIN), please submit a “
                <Link href={'https://1099portal.libertymutual.com/form/2'} target='_blank' >
                  Request Additional Information
                </Link>
                ” request at the top of this page. Please include a signed W-9 if the correction involves your name or
                TIN.
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>How long does it take to receive a corrected 1099?</h3>
            <br />
            <ul>
              <li>
                Corrected 1099s are typically processed within 5 business days. You will receive a copy by email if we
                have one on file for you, or by mail if not.
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>What should I do if I believe a payment was incorrectly reported on my 1099?</h3>
            <br />
            <ul>
              <li>
                Please submit a “
                <Link href={'https://1099portal.libertymutual.com/form/2'} target='_blank' >
                  Request Additional Information
                </Link>
                ” request at the top of this page so we can research the payment and issue a correction if needed.
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>What should I do if my company is exempt from 1099 reporting?</h3>
            <br />
            <ul>
              <li>
                Please submit a signed W-9 form showing your correct information (exempt recipients should ensure that
                Box 4, the exemption box, is fully completed), along with a copy of the <b>incorrect 1099</b>. You can submit
                these documents at the top of this page under “
                <Link href={'https://1099portal.libertymutual.com/form/1'} target='_blank' >
                  Request a Name or Tax ID Change
                </Link>
                .” For a blank W-9, please visit the <Link href={'https://www.irs.gov/pub/irs-pdf/fw9.pdf'} target='_blank' >IRS website</Link>.
              </li>
            </ul>
          </span>
        </BodyText>
      ),
    },
    {
      id: 3,
      title: 'Special Situations',
      desc: (
        <BodyText type='default'>
          <span>
            <br />
            <h3>
              I am an attorney. Why is there money reporting in Box 1 on Form 1099-NEC or Box 10 on Form 1099-MISC?
            </h3>
            <br />
            <ul>
              <li>
                Attorney fees must be reported as nonemployee compensation in Box 1 on Form 1099-NEC. Settlement
                payments, however, should be reported in Box 10, “Gross Proceeds Paid to an Attorney,” on Form
                1099-MISC. If the payment shown in Box 1 on your 1099-NEC is a settlement payment, please submit a “
                <Link href={'https://1099portal.libertymutual.com/form/2'} target='_blank' >
                  Request Additional Information
                </Link>
                ” request at the top of this page.
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>I received a 1099 from Liberty Mutual, but I do not do business with Liberty Mutual.</h3>
            <br />
            <ul>
              <li>
                We issued a payment to you from Liberty Mutual or one of our subsidiaries that is considered reportable
                income according to IRS guidelines. The IRS requires Liberty Mutual to file Forms 1099-MISC, 1099-NEC,
                and 1099-INT for reportable income paid to non-employees. If you believe you received this form in
                error, please submit a “
                <Link href={'https://1099portal.libertymutual.com/form/2'} target='_blank' >
                  Request Additional Information
                </Link>
                ” request at the top of this page.
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>
              Why did I receive a 1099 from Liberty Mutual Group Inc. when I do business with Helmsman Management
              Services?
            </h3>
            <br />
            <ul>
              <li>
                You may have received payments from Helmsman Management Services, but your 1099 will be issued under
                Liberty Mutual Group Inc. For tax reporting purposes, Helmsman reports through Liberty Mutual.
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>I received more than one 1099. Why?</h3>
            <br />
            <ul>
              <li>
                You may receive more than one 1099 if you were paid by more than one Liberty Mutual entity (for example,
                both Liberty Mutual Group Inc. and Helmsman Management Services), or if you received different types of
                reportable income that must be reported on separate forms (such as 1099-NEC and 1099-INT).
              </li>
            </ul>
          </span>
        </BodyText>
      ),
    },
    {
      id: 4,
      title: 'Help and Contact',
      desc: (
        <BodyText type='default'>
          <span>
            <br />
            <h3>Who should I contact with questions about my 1099?</h3>
            <br />
            <ul>
              <li>
                For questions about your 1099, please submit the appropriate request at the top of this page, or email
                us at <Link href='mailto:1099LM@LibertyMutual.com'>1099LM@LibertyMutual.com</Link>.
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>What information should I provide when contacting support?</h3>
            <br />
            <ul>
              <li>
                To help us respond quickly, please have the following ready: your full name (or business name), your TIN
                or the last four digits of your SSN/EIN, the tax year in question, a brief description of your request,
                and a copy of the W-9 on file for the 1099 in question.
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>Can Liberty Mutual provide tax advice?</h3>
            <br />
            <ul>
              <li>
                No. We can help you understand what’s reported in your 1099 and correct errors, but we cannot provide
                tax advice. For questions about how to report this income on your tax return, please consult a qualified
                tax professional or the IRS.
              </li>
            </ul>
          </span>

          <span>
            <br />
            <h3>Where can I find IRS guidance about Form 1099?</h3>
            <br />
            <ul>
              <li>
                General IRS information about Form 1099 — including instructions for 1099-MISC, 1099-NEC, and 1099-INT —
                is available at{' '}
                <Link href='https://www.irs.gov/forms-pubs/about-form-1099' target='_blank' >
                  irs.gov/Form1099
                </Link>
                .
              </li>
            </ul>
          </span>
        </BodyText>
      ),
    },
  ]

  return (
    <div>
      <Heading type={'h3-light'} truncate>
        Frequently Asked Questions
      </Heading>
      <Accordion as='h4' className='faq component'>
        {faqList?.map(f => (
          <AccordionTab labelVisual={f.title} key={`faq-${f.id}`}>
            {f.desc}
          </AccordionTab>
        ))}
      </Accordion>
    </div>
  )
}

export default FAQ
